import { skipToken, useMutation, useQuery } from '@tanstack/react-query';
import { useRouter } from '@tanstack/react-router';
import {
  Button,
  Flex,
  Heading,
  Icon,
  Link,
  Modal,
  ModalBody,
  ModalFooter,
  Paragraph,
  Spinner,
  useSnackbars,
  type PlumaManagedModalProps,
} from '@customerio/pluma-components/react';
import { exportKeys, isExportComplete, type Export } from '@/services/db/export/entity';
import { downloadExport, getExport } from '@/services/db/export/queries';
import * as styles from './export-status-modal.css';

// "Close", not "Cancel": dismissing the modal doesn't stop the queued export
// (same reasoning as the unsuppress-confirm modal's no-matches state).
const DISMISS_LABEL = 'Close';

const POLL_INTERVAL_MS = 2000;
// Give up polling after this many consecutive fetch failures so a persistently
// failing record surfaces as an error instead of spinning forever.
const MAX_POLL_ERRORS = 5;

// Polling stops once the export settled: ready (download path set) or failed.
function isExportSettled(record: Export): boolean {
  return isExportComplete(record) || record.failed === true;
}

export type ExportStatusModalData = {
  workspaceId: number;
  /** null while the export is still being queued (no id yet). */
  exportId: string | null;
};

/**
 * Live export status: polls the queued export and turns into a Download button
 * when the file is ready — no email round-trip, no trip to the exports page.
 * Opened before the export POST resolves (spinner, `exportId` null); the caller
 * feeds the id in via `modals.update` once the export is queued.
 */
export const ExportStatusModal = ({ data, onClose, modalState }: PlumaManagedModalProps<ExportStatusModalData>) => {
  const { workspaceId, exportId } = data;
  const router = useRouter();
  const snackbars = useSnackbars();

  const pollQuery = useQuery({
    queryKey: exportKeys.detail(workspaceId, exportId ?? 'queueing'),
    queryFn: exportId == null ? skipToken : () => getExport(workspaceId, exportId),
    // Tolerate transient fetch errors, but stop and surface a failure once
    // they're persistent so the modal doesn't spin forever.
    retry: MAX_POLL_ERRORS - 1,
    retryDelay: POLL_INTERVAL_MS,
    refetchInterval: (query) => {
      if (query.state.status === 'error') return false;
      const record = query.state.data;
      if (record != null && isExportSettled(record)) return false;
      return POLL_INTERVAL_MS;
    },
    // Fresh state per open: a new export must never show the previous one's
    // result (mirrors the Ember modal's reset-on-id-change behavior).
    staleTime: 0,
    gcTime: 0,
  });

  const download = useMutation({
    mutationFn: (id: string) => downloadExport(workspaceId, id),
    onError: () => {
      snackbars.create({
        variant: 'critical',
        id: 'people.export.download',
        message: "The download couldn't be started. Try again or contact support.",
      });
    },
  });

  const exportRecord = pollQuery.data;
  const isReady = exportRecord != null && isExportComplete(exportRecord);
  const isFailed = pollQuery.isError || exportRecord?.failed === true;

  const exportsUrl = router.buildLocation({
    to: '/workspaces/$workspaceId/exports',
    params: { workspaceId },
  }).href;

  return (
    <Modal state={modalState} title="Export to CSV" onClose={onClose} className={styles.modal}>
      <ModalBody>
        <Flex direction="column" alignItems="center" gap="150" py="300">
          {isFailed ? (
            <>
              {/* The v2 icon set renames Ember's `warning` to `caution`. */}
              <Icon name="caution" color="critical" size="xl" />
              <Heading level="3">Export failed</Heading>
              <Paragraph color="subtle" textAlign="center">
                Your export couldn't be processed. Close this and try again.
              </Paragraph>
            </>
          ) : isReady ? (
            <>
              <Icon name="check-circle" color="success" size="xl" />
              <Heading level="3">Your export is ready</Heading>
              <Paragraph color="subtle" textAlign="center">
                Download the CSV below or check your email for a download link.
              </Paragraph>
            </>
          ) : (
            <>
              <Spinner />
              <Heading level="3">Preparing your export</Heading>
              <Paragraph color="subtle" textAlign="center">
                This usually takes a moment. We will email you when the export is ready, and it will be available on the{' '}
                <Link href={exportsUrl} variant="secondary" isUnderlined isExternal withSafeExternalAttributes={false}>
                  exports page
                </Link>
                . You can close this at any time.
              </Paragraph>
            </>
          )}
        </Flex>
      </ModalBody>

      <ModalFooter>
        <Button variant="secondary" onClick={onClose}>
          {DISMISS_LABEL}
        </Button>
        {isReady ? (
          <Button
            variant="primary"
            ml="100"
            icon="download"
            isLoading={download.isPending}
            onClick={() => {
              if (exportId != null) download.mutate(exportId);
            }}
          >
            Download
          </Button>
        ) : null}
      </ModalFooter>
    </Modal>
  );
};
